/* eslint-disable no-console */
/* eslint-disable no-use-before-define */
import React, {
  useEffect,
  useReducer,
  createContext,
  useContext,
  useCallback,
  useRef,
} from 'react';
import { message } from 'antd';
import { useRequest } from 'ahooks';
import { getDriveList, getDirectoryContents } from 'services/transfer';
import LocaleContext from 'locales';
import { fileExploreReducer, initialState, ActionTypes } from './fileExploreReducer';
import { buildTreeData } from './utils/treeUtils';

export default function useFileExplore() {
  // 이미 진행 중인 API 호출을 추적
  const pendingApiCalls = useRef(new Set());

  // 상태 관리
  const [state, dispatch] = useReducer(fileExploreReducer, initialState);
  const userId = localStorage.getItem('userId');
  const { locale } = useContext(LocaleContext);

  // 업로드 완료 이벤트 리스너 추가
  useEffect(() => {
    const handleUploadComplete = (e) => {
      const { path } = e.detail;
      // 현재 경로와 업로드된 경로가 같으면 디렉토리 내용 새로고침
      if (path === state.currentPath) {
        loadDirectoryContents(path, {
          forceRefresh: true, // 캐시를 무시하고 새로 로드한다.ㅇ
          updateListItems: true,
        });
      }
    };

    window.addEventListener('uploadComplete', handleUploadComplete);

    return () => {
      window.removeEventListener('uploadComplete', handleUploadComplete);
    };
  }, [state.currentPath, loadDirectoryContents]);

  /**
   * 폴더 구조를 UI 트리에 표시할 수 있는 형태로 변환하는 함수
   *
   * 언제 사용되는가:
   * 1. 드라이브 목록이 로드될 때
   * 2. 폴더 구조(folderStructure)가 변경될 때마다 useEffect를 통해 자동 호출
   * 3. 트리 노드를 확장할 때 새로운 폴더 정보가 로드된 후
   *
   * 역할:
   * - 폴더 구조 데이터를 계층적 트리 구조로 변환
   * - 드라이브(루트 노드)와 하위 폴더들의 계층 관계 설정
   * - 최종 데이터를 NavigationPane의 트리 컴포넌트에서 사용할 수 있는 형태로 구성
   */
  const updateTreeData = useCallback(() => {
    const { folderStructure, drives } = state;

    // 드라이브가 없으면 빈 배열 반환
    if (!drives || drives.length === 0) {
      dispatch({ type: ActionTypes.SET_FOLDERS, payload: [] });
      return;
    }

    // 트리 데이터 생성
    const treeData = buildTreeData(folderStructure, drives);

    // 트리 데이터 업데이트 - 항상 최신 데이터로 업데이트
    dispatch({ type: ActionTypes.SET_FOLDERS, payload: treeData });
  }, [state.folderStructure, state.drives]);
  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (state.drives.length > 0) {
      // 상태 변경 후에 그 변경된 상태를 사용하도록 setTimeout 추가
      const timerId = setTimeout(() => {
        updateTreeData();
      }, 0);

      return () => {
        clearTimeout(timerId);
      };
    }
  }, [state.folderStructure, state.drives, updateTreeData]);

  // 폴더 구조 업데이트 함수
  const updateFolderStructure = useCallback((path, folders) => {
    const formattedFolders = folders.map((folder) => ({
      key: `${path === '/' ? '' : path}/${folder.name}`.replace(/\/+/g, '/'),
      title: folder.name,
      path: `${path === '/' ? '' : path}/${folder.name}`.replace(/\/+/g, '/'),
      isLeaf: false,
    }));

    // 기존 폴더 구조에 새 폴더 추가
    dispatch({
      type: ActionTypes.SET_FOLDER_STRUCTURE,
      payload: (prevStructure) => ({
        ...prevStructure,
        [path]: formattedFolders,
      }),
    });
  }, []);

  // 디렉토리 내용 로드 함수
  const loadDirectoryContents = useCallback(
    async (path, options = {}) => {
      const callKey = `fetch_${path}`; // 중복 체크에 사용

      // 1. 이미 진행 중인 API 호출 확인
      if (pendingApiCalls.current.has(callKey)) {
        return { pending: true };
      }

      // 2. 이미 로드된 경우 캐시 사용
      if (state.loadedKeys.includes(path) && options.forceRefresh !== true) {
        // ListPane 업데이트가 필요한 경우, 캐시된 데이터를 사용
        if (options.updateListItems === true) {
          // items 상태를 업데이트 (캐시된 데이터가 있다면)
          try {
            // 캐시된 데이터를 사용하여 ListPane 업데이트
            dispatch({ type: ActionTypes.SET_CURRENT_PATH, payload: path });

            // API 호출 없이 반환
            return { cached: true };
          } catch (error) {
            console.error('캐시된 데이터 사용 중 오류:', error);
            // 캐시 사용 중 오류 발생 시 계속 진행하여 새로 로드
          }
        } else {
          // ListPane 업데이트가 필요 없는 경우 그냥 반환
          return { cached: true };
        }
      }

      // API 호출 시작 - 진행 중 표시
      dispatch({ type: ActionTypes.SET_LOADING, payload: true });
      pendingApiCalls.current.add(callKey);

      try {
        const { data: response } = await getDirectoryContents(path);

        if (!response || !response.success) {
          throw new Error(response?.errorMessage || 'unknown-error');
        }

        const itemsData = response.data || [];

        // * 리스트 데이터 업데이트 (옵션에 따라)
        if (options.updateListItems === true) {
          const processedItems = processItems(path, itemsData);
          dispatch({ type: ActionTypes.SET_ITEMS, payload: processedItems });
        }

        // * 폴더 구조 업데이트 (항상)
        const folders = itemsData.filter((item) => item.type === 'dir');
        updateFolderStructure(path, folders);

        // 로드된 키에 추가 (중복 방지)
        dispatch({
          type: ActionTypes.SET_LOADED_KEYS,
          payload: (prevKeys) => {
            if (prevKeys.includes(path)) return prevKeys;
            return [...prevKeys, path];
          },
        });

        // 확장 키 업데이트 (옵션에 따라)
        if (options.updateExpandedKeys) {
          dispatch({
            type: ActionTypes.SET_EXPANDED_KEYS,
            payload: (prevKeys) => {
              if (prevKeys.includes(path)) return prevKeys;
              return [...prevKeys, path];
            },
          });
        }

        return { success: true, data: itemsData };
      } catch (error) {
        if (!options.suppressError) {
          showFolderLoadError(locale.resource['txt.file-transfer.msg-load-folder-contents-failed']);
        }

        return { success: false, error };
      } finally {
        // API 호출 완료
        pendingApiCalls.current.delete(callKey);
        dispatch({ type: ActionTypes.SET_LOADING, payload: false });
      }
    },
    [state.loadedKeys, processItems, updateFolderStructure, locale.resource],
  );

  // 드라이브 목록 조회 API
  const fetchDrives = useRequest(() => getDriveList(userId), {
    manual: true,
    onSuccess: ({ data: response }) => {
      if (response && response.status === 'success') {
        const drives = response.data || [];
        dispatch({ type: ActionTypes.SET_DRIVES, payload: drives });

        if (drives.length > 0) {
          const firstDrive = drives[0];
          const rootPath = firstDrive.drivePath || '/';

          // 초기 상태 업데이트
          dispatch({ type: ActionTypes.SET_SELECTED_DRIVE, payload: firstDrive });
          dispatch({ type: ActionTypes.SET_SELECTED_KEYS, payload: [rootPath] });
          dispatch({ type: ActionTypes.SET_CURRENT_PATH, payload: rootPath });
          dispatch({ type: ActionTypes.SET_EXPANDED_KEYS, payload: [rootPath] });

          // 초기 데이터 로드 - 중복 호출은 함수 내부에서 방지됨
          loadDirectoryContents(rootPath, {
            updateListItems: true,
            updateExpandedKeys: false, // 이미 위에서 업데이트했으므로 false
          });
        }
      }
    },
    onError: (error) => {
      console.error('드라이브 목록 조회 실패', error);
      message.error(locale.resource['txt.file-transfer.msg-load-drives-failed']);
      dispatch({ type: ActionTypes.SET_ERROR, payload: error.message });
    },
  });

  // 아이템 데이터 가공 함수
  const processItems = useCallback((basePath, items) => {
    return Array.isArray(items)
      ? items.map((item) => ({
          key: `${basePath === '/' ? '' : basePath}/${item.name}`.replace(/\/+/g, '/'),
          name: item.name,
          type: item.type,
          size: item.size || 0,
          whenModified: item.updateDate || new Date().toISOString(),
          path: `${basePath === '/' ? '' : basePath}/${item.name}`.replace(/\/+/g, '/'),
          ...item,
        }))
      : [];
  }, []);

  // 컴포넌트 마운트 시 드라이브 목록 조회
  useEffect(() => {
    fetchDrives.run();
  }, []);

  // 폴더 선택 함수 (ListPane의 폴더 더블클릭, 브레드크럼으로 이동 시)
  const selectFolder = useCallback(
    async (path) => {
      // 이미 API 호출 중인 경우 중복 호출 방지
      const callKey = `fetch_${path}`;
      if (pendingApiCalls.current.has(callKey)) {
        return;
      }

      // 로딩 상태 설정
      pendingApiCalls.current.add(callKey);
      dispatch({ type: ActionTypes.SET_LOADING, payload: true });

      try {
        // 직접 API 호출
        const { data: response } = await getDirectoryContents(path);

        if (!response || !response.success) {
          throw new Error(response?.errorMessage || 'txt.file-transfer.msg-unknown-error');
        }

        const itemsData = response.data || [];

        // API 호출 성공 후에만 현재 경로와 선택된 키 업데이트
        dispatch({ type: ActionTypes.SET_SELECTED_KEYS, payload: [path] });
        dispatch({ type: ActionTypes.SET_CURRENT_PATH, payload: path });

        // 선택한 경로의 드라이브 정보 찾기
        const driveInfo = findDriveByPath(path, state.drives);
        if (driveInfo) {
          dispatch({ type: ActionTypes.SET_SELECTED_DRIVE, payload: driveInfo });
        }

        // 폴더 확장 키에 추가
        if (!state.expandedKeys.includes(path)) {
          dispatch({
            type: ActionTypes.SET_EXPANDED_KEYS,
            payload: [...state.expandedKeys, path],
          });
        }

        // 현재 경로의 항목 업데이트 (파일 + 폴더)
        const processedItems = processItems(path, itemsData);
        dispatch({ type: ActionTypes.SET_ITEMS, payload: processedItems });

        // 폴더만 필터링 - NavigationPane용
        const folders = itemsData.filter((item) => item.type === 'dir');

        // 폴더 구조 업데이트
        updateFolderStructure(path, folders);

        // 로드된 키 목록에 현재 경로 추가
        dispatch({
          type: ActionTypes.SET_LOADED_KEYS,
          payload: (prevKeys) => [...new Set([...prevKeys, path])],
        });

        updateTreeData();
      } catch (error) {
        console.error('폴더 이동 오류:', error);
        message.error(locale.resource['txt.file-transfer.msg-load-folder-contents-failed']);

        // 실패 시에도 중복 API 호출 방지를 위해 loadedKeys에 추가
        dispatch({
          type: ActionTypes.SET_LOADED_KEYS,
          payload: (prevKeys) => [...new Set([...prevKeys, path])],
        });
      } finally {
        pendingApiCalls.current.delete(callKey);
        dispatch({ type: ActionTypes.SET_LOADING, payload: false });
      }
    },
    [
      state.expandedKeys,
      state.drives,
      processItems,
      locale.resource,
      updateTreeData,
      updateFolderStructure,
      findDriveByPath,
    ],
  );
  // 트리 확장 핸들러
  const handleTreeExpand = useCallback((expandedKeys, _info) => {
    // const isExpanding = info.expanded;
    // const { path } = info.node;

    // 확장된 키 업데이트
    dispatch({ type: ActionTypes.SET_EXPANDED_KEYS, payload: expandedKeys });
  }, []);

  const handleLoadData = useCallback(
    (treeNode) => {
      const { key: nodePath } = treeNode;

      // 이미 로드된 경우 즉시 완료
      if (state.loadedKeys.includes(nodePath)) {
        return Promise.resolve();
      }

      // 이미 API 호출 중인 경우
      const callKey = `fetch_${nodePath}`;
      if (pendingApiCalls.current.has(callKey)) {
        return new Promise((resolve) => {
          const checkInterval = setInterval(() => {
            if (!pendingApiCalls.current.has(callKey)) {
              clearInterval(checkInterval);
              resolve();
            }
          }, 100);

          // 최대 대기 시간 설정 (5초)
          setTimeout(() => {
            clearInterval(checkInterval);
            resolve();
          }, 5000);
        });
      }

      pendingApiCalls.current.add(callKey);
      // getDirectoryContents를 직접 호출하고 그 Promise를 반환
      return new Promise((resolve) => {
        getDirectoryContents(nodePath)
          .then(({ data: response }) => {
            if (!response || !response.success) {
              throw new Error(response?.errorMessage || 'unknown-error');
            }

            const itemsData = response.data || [];
            const folders = itemsData.filter((item) => item.type === 'dir');

            // 폴더 구조 업데이트
            updateFolderStructure(nodePath, folders);

            // 로드된 키 목록에 추가
            dispatch({
              type: ActionTypes.SET_LOADED_KEYS,
              payload: (prevKeys) => [...new Set([...prevKeys, nodePath])],
            });

            // 트리 데이터 업데이트
            updateTreeData();
          })
          .catch((error) => {
            console.error('폴더 내용 조회 실패', error);
            showFolderLoadError(
              locale.resource['txt.file-transfer.msg-load-folder-contents-failed'],
            );
            // 오류가 발생해도 로드된 것으로 표시하여 무한 루프 방지
            dispatch({
              type: ActionTypes.SET_LOADED_KEYS,
              payload: (prevKeys) => [...new Set([...prevKeys, nodePath])],
            });
          })
          .finally(() => {
            pendingApiCalls.current.delete(callKey);
            resolve(); // 항상 Promise를 완료하여 트리 UI가 대기 상태에 멈추지 않도록 함
          });
      });
    },
    [state.loadedKeys, updateFolderStructure, updateTreeData, locale.resource],
  );
  const handleTreeSelect = useCallback(
    async (selectedKeys, info) => {
      const isNoSelection = selectedKeys.length === 0;
      const isDeselected = !info.selected;
      const isTreeSelectionCanceled = isNoSelection || isDeselected;
      const { path } = info.node;

      const isAlreadySelected = state.selectedKeys.length === 1 && state.selectedKeys[0] === path;

      if (isTreeSelectionCanceled || isAlreadySelected) {
        return;
      }

      // 이미 API 호출 중인 경우 중복 호출 방지
      const callKey = `fetch_${path}`;
      if (pendingApiCalls.current.has(callKey)) {
        return;
      }

      // 폴더 내용 로딩 - 로딩 상태 설정
      pendingApiCalls.current.add(callKey);
      dispatch({ type: ActionTypes.SET_LOADING, payload: true });

      try {
        const { data: response } = await getDirectoryContents(path);

        if (!response || !response.success) {
          throw new Error(response?.errorMessage || 'txt.file-transfer.msg-unknown-error');
        }

        const itemsData = response.data || [];

        // API 호출 성공 후 현재 경로와 선택된 키 업데이트
        dispatch({ type: ActionTypes.SET_SELECTED_KEYS, payload: [path] });
        dispatch({ type: ActionTypes.SET_CURRENT_PATH, payload: path });

        // 선택한 경로의 드라이브 정보 찾기
        const driveInfo = findDriveByPath(path, state.drives);
        if (driveInfo) {
          dispatch({ type: ActionTypes.SET_SELECTED_DRIVE, payload: driveInfo });
        }

        // 폴더가 축소되어 있는 경우에만 확장
        if (!state.expandedKeys.includes(path)) {
          dispatch({
            type: ActionTypes.SET_EXPANDED_KEYS,
            payload: [...state.expandedKeys, path],
          });
        }

        // 현재 경로의 항목 업데이트 (파일 + 폴더)
        const processedItems = processItems(path, itemsData);
        dispatch({ type: ActionTypes.SET_ITEMS, payload: processedItems });

        // 폴더만 필터링 - NavigationPane용
        const folders = itemsData.filter((item) => item.type === 'dir');

        // 폴더 구조 업데이트
        updateFolderStructure(path, folders);

        // 로드된 키 목록에 현재 경로 추가
        dispatch({
          type: ActionTypes.SET_LOADED_KEYS,
          payload: (prevKeys) => [...new Set([...prevKeys, path])],
        });

        updateTreeData();
      } catch (error) {
        console.error('폴더 내용 조회 실패', error);
        message.error(locale.resource['txt.file-transfer.msg-load-folder-contents-failed']);
        dispatch({
          type: ActionTypes.SET_ERROR,
          payload: locale.resource[error.message] || error.message,
        });

        // handleLoadData에서 중복 호출을 하지 않기 위함
        dispatch({
          type: ActionTypes.SET_LOADED_KEYS,
          payload: (prevKeys) => [...new Set([...prevKeys, path])],
        });
      } finally {
        pendingApiCalls.current.delete(callKey);
        dispatch({ type: ActionTypes.SET_LOADING, payload: false });
      }
    },
    [
      state.selectedKeys,
      state.expandedKeys,
      state.loadedKeys,
      state.folderStructure,
      state.drives,
      processItems,
      locale.resource,
      updateTreeData,
      updateFolderStructure,
    ],
  );

  const showFolderLoadError = (errorMsg) => {
    message.open({
      type: 'error',
      content: errorMsg,
      key: 'folder-load-error',
      duration: 3,
    });
  };

  // 경로에 해당하는 드라이브 찾기
  const findDriveByPath = (path, drives) => {
    if (!drives || drives.length === 0 || !path) return null;

    // 경로가 드라이브 경로와 일치하거나 드라이브 경로로 시작하는 드라이브 찾기
    return drives.find(
      (drive) =>
        path === drive.drivePath ||
        path.startsWith(`${drive.drivePath}/`) ||
        (drive.drivePath === '/' && path.startsWith('/')),
    );
  };

  // 새로고침 함수
  const refreshDrives = useCallback(() => {
    // 모든 상태 초기화
    dispatch({ type: ActionTypes.SET_FOLDER_STRUCTURE, payload: {} });
    dispatch({ type: ActionTypes.SET_EXPANDED_KEYS, payload: [] });
    dispatch({ type: ActionTypes.SET_LOADED_KEYS, payload: [] });

    // 드라이브 목록 다시 로드
    fetchDrives.run();
  }, [fetchDrives]);

  return {
    state,
    dispatch,
    fetchDrives,
    selectFolder,
    handleTreeExpand,
    handleTreeSelect,
    updateTreeData,
    handleLoadData,
    refreshDrives,
  };
}

// 컨텍스트 생성
const FileExploreContext = createContext(null);

export function FileExploreProvider({ children }) {
  const fileExploreState = useFileExplore();

  return (
    <FileExploreContext.Provider value={fileExploreState}>{children}</FileExploreContext.Provider>
  );
}

// 컨텍스트 사용을 위한 훅
export function useFileExploreContext() {
  const context = useContext(FileExploreContext);

  if (!context) {
    throw new Error('useFileExploreContext must be used within a FileExploreProvider');
  }
  return context;
}
